/**
 * EscheatmentHandoff — Missouri State Treasurer unclaimed-property handoff.
 *
 * Port of legacy SPFx `SdbManagementScreen.loadEscheatment` (SPFx src:
 * `accountOpeningV2/components/screens/SdbManagementScreen.tsx:290-316`)
 * with all hardcoded year values removed. The config-driven service
 * (`EscheatmentService.loadRule`) is the only source of abandonment
 * periods; if the `/v3/config/escheatment-rules` seed has not landed
 * yet, this flow renders "—" for every computed field (never a guess).
 *
 * Invoked from:
 *   - `CloseOutWizard` step 4 (drilled disposition = escheat)
 *   - Standalone entry via deep link from RSM dashboard for deceased
 *     renters whose contents are staged for November state handoff
 *
 * Handoff target:
 *   `apiClient.retail.events.write({ eventType: 'sdb-escheatment-queued' })`
 *   `apiClient.retail.tasks.create({ type: 'sdb-escheatment' })` → RCC
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AlertTriangle, ArrowLeft, CheckCircle2 } from '@retail-ops/panel-shell/fi';
import {
  Button,
  Pill,
  WidgetCard,
  KVPair,
  CardRadioGroup,
} from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSdbDetail } from '../hooks/useSdbDetail';
import {
  EscheatmentService,
  type EscheatType,
  type EscheatmentHandoffResult,
  type EscheatmentRule,
} from '../services/EscheatmentService';
import '../rsm.css';

interface FormState {
  escheatType: EscheatType;
  triggerEventDate: string;
  lastContactDate: string;
  contentsDescription: string;
  estimatedValueInput: string;
  dueDiligenceSent: boolean;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function EscheatmentHandoff(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();

  const { data: box, loading: boxLoading, error: boxError } = useSdbDetail(acctId);

  const [form, setForm] = useState<FormState>({
    escheatType: 'drilled',
    triggerEventDate: todayIso(),
    lastContactDate: '',
    contentsDescription: '',
    estimatedValueInput: '',
    dueDiligenceSent: false,
  });

  const [rule, setRule] = useState<EscheatmentRule | null>(null);
  const [ruleLoading, setRuleLoading] = useState(false);
  const [ruleChecked, setRuleChecked] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<EscheatmentHandoffResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const svc = useMemo(() => new EscheatmentService(apiClient), [apiClient]);

  // Load rule preview whenever escheat type changes.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setRuleLoading(true);
    setRuleChecked(false);
    void svc
      .loadRule('MO', form.escheatType === 'deceased' ? 'sdb-deceased' : 'sdb-drilled', {
        signal: ctrl.signal,
      })
      .then((r) => {
        if (!cancelled) {
          setRule(r);
          setRuleChecked(true);
          setRuleLoading(false);
        }
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [form.escheatType, svc]);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const canSubmit =
    !!user &&
    !!acctId &&
    form.triggerEventDate.length > 0 &&
    form.contentsDescription.trim().length > 0 &&
    !submitting;

  const submit = async (): Promise<void> => {
    if (!user || !acctId) return;
    setSubmitting(true);
    setError(null);
    try {
      const parsedValue = form.estimatedValueInput.trim()
        ? Number.parseFloat(form.estimatedValueInput)
        : NaN;
      const handoff = await svc.queueHandoff({
        acctId,
        boxNumber: box?.boxNumber ?? null,
        branchId: box?.branchId ?? null,
        escheatType: form.escheatType,
        primaryCifId: box?.primaryCifId ?? null,
        primaryCustomerName: box?.primaryCustomerName ?? null,
        triggerEventDate: form.triggerEventDate,
        lastContactDate: form.lastContactDate || null,
        contentsDescription: form.contentsDescription,
        estimatedValue: Number.isFinite(parsedValue) ? parsedValue : null,
        dueDiligenceSent: form.dueDiligenceSent,
        authorizedBy: user.upn,
      });
      setResult(handoff);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Escheatment handoff failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (boxLoading) {
    return (
      <div className="rsm-detail">
        <p>Loading box {acctId}…</p>
      </div>
    );
  }
  if (boxError || !box) {
    return (
      <div className="rsm-detail">
        <div className="rsm-banner rsm-banner--err" role="alert">
          {boxError ?? 'Box not found.'}
        </div>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="rsm-wizard">
      <div className="rsm-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to box
        </Button>
        <h1>Escheatment handoff · Box {box.boxNumber ?? box.acctId}</h1>
        <Pill variant="warn">MO State Treasurer</Pill>
      </div>

      {result ? (
        <WidgetCard title="Handoff queued" stripe="approvals">
          <div className="rsm-banner rsm-banner--ok" role="status">
            <CheckCircle2 size={16} className="rsm-inline-icon" />
            <div>
              RCC has been notified. Audit event {result.eventId ?? '(pending)'}; task{' '}
              {result.taskId ?? '(pending)'}.
            </div>
          </div>
          <div className="rsm-detail__kvgrid">
            <KVPair
              label="Abandonment period"
              value={result.abandonmentYears == null ? '—' : `${result.abandonmentYears} years`}
            />
            <KVPair label="Report due" value={result.reportDueDate ?? '—'} />
            <KVPair
              label="Days remaining"
              value={result.daysUntilReport == null ? '—' : String(result.daysUntilReport)}
            />
            <KVPair label="Rule citation" value={result.rule?.citation ?? '—'} />
          </div>
          <div className="rsm-wizard__footer">
            <Button variant="ghost" onClick={() => navigate('..')}>
              Back to box
            </Button>
          </div>
        </WidgetCard>
      ) : (
        <>
          <WidgetCard title="Rule preview (from /v3/config/escheatment-rules)" stripe="pipeline">
            {ruleLoading && <p>Loading rule…</p>}
            {!ruleLoading && ruleChecked && !rule && (
              <div className="rsm-banner rsm-banner--info">
                <AlertTriangle size={14} className="rsm-inline-icon" />
                <div>
                  No rule seeded for state MO /{' '}
                  {form.escheatType === 'deceased' ? 'sdb-deceased' : 'sdb-drilled'}. Deadline
                  will render "—" until <code>/v3/config/escheatment-rules</code> is seeded.
                </div>
              </div>
            )}
            {!ruleLoading && rule && (
              <div className="rsm-detail__kvgrid">
                <KVPair label="Jurisdiction" value={rule.jurisdictionState} />
                <KVPair label="Property type" value={rule.propertyType} />
                <KVPair label="Abandonment years" value={String(rule.abandonmentYears)} />
                <KVPair
                  label="Report deadline (MM-DD)"
                  value={rule.reportDeadlineMonthDay ?? '—'}
                />
                <KVPair
                  label="Due-diligence minimum"
                  value={
                    rule.dueDiligenceMinValue == null
                      ? '—'
                      : rule.dueDiligenceMinValue.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })
                  }
                />
                <KVPair label="Citation" value={rule.citation ?? '—'} />
              </div>
            )}
          </WidgetCard>

          <WidgetCard title="Handoff detail" stripe="approvals">
            <CardRadioGroup<EscheatType>
              name="escheat-type"
              selected={form.escheatType}
              onChange={(v) => update('escheatType', v)}
              options={[
                {
                  value: 'drilled',
                  primary: 'Drilled contents',
                  helper:
                    'Box drilled for non-payment. Contents staged in vault awaiting state handoff.',
                },
                {
                  value: 'deceased',
                  primary: 'Deceased renter',
                  helper:
                    'Renter is deceased, no estate claim received. Contents held per state deceased-renter period.',
                },
              ]}
            />
            <div className="rsm-toolbar">
              <div className="rsm-field">
                <label htmlFor="esc-trigger">Trigger event date</label>
                <input
                  id="esc-trigger"
                  type="date"
                  value={form.triggerEventDate}
                  onChange={(e) => update('triggerEventDate', e.target.value)}
                />
              </div>
              <div className="rsm-field">
                <label htmlFor="esc-contact">Last contact attempt (optional)</label>
                <input
                  id="esc-contact"
                  type="date"
                  value={form.lastContactDate}
                  onChange={(e) => update('lastContactDate', e.target.value)}
                />
              </div>
              <div className="rsm-field">
                <label htmlFor="esc-value">Estimated contents value (optional)</label>
                <input
                  id="esc-value"
                  type="number"
                  value={form.estimatedValueInput}
                  onChange={(e) => update('estimatedValueInput', e.target.value)}
                  placeholder="USD"
                />
              </div>
            </div>
            <div className="rsm-field">
              <label htmlFor="esc-desc">Contents description</label>
              <textarea
                id="esc-desc"
                value={form.contentsDescription}
                onChange={(e) => update('contentsDescription', e.target.value)}
                placeholder="Summarize contents staged for state handoff (supplement to printed Record of Contents)."
              />
            </div>
            <label className="rsm-check-row rsm-check-row--close">
              <input
                type="checkbox"
                checked={form.dueDiligenceSent}
                onChange={(e) => update('dueDiligenceSent', e.target.checked)}
              />
              Due-diligence mailing sent to last-known address.
            </label>
          </WidgetCard>

          {error && (
            <div className="rsm-banner rsm-banner--err" role="alert">
              {error}
            </div>
          )}

          <div className="rsm-wizard__footer">
            <Button variant="ghost" onClick={() => navigate('..')}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit}>
              {submitting ? 'Queueing…' : 'Queue handoff to RCC'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
